import { __rest, __assign } from 'tslib';
import React, { useRef, useState, useCallback, Fragment } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { getDataTestId } from '@alfalab/core-components-shared/esm';
import { StatusBadge } from '@alfalab/core-components-status-badge/esm';
import { useFocus, useLayoutEffect_SAFE_FOR_SSR } from '@alfalab/hooks';
import { ClearButton } from '../clear-button/Component.js';

var defaultColors = {"input":"input__input_1iu3n","hasInnerLabel":"input__hasInnerLabel_1iu3n","disableUserInput":"input__disableUserInput_1iu3n","error":"input__error_1iu3n"};
require('./default.css')

var styles = {"input":"input__input_14gtk","size-40":"input__size-40_14gtk","hasInnerLabel":"input__hasInnerLabel_14gtk","size-48":"input__size-48_14gtk","size-56":"input__size-56_14gtk","size-64":"input__size-64_14gtk","size-72":"input__size-72_14gtk","block":"input__block_14gtk","errorIcon":"input__errorIcon_14gtk","successIcon":"input__successIcon_14gtk","disableUserInput":"input__disableUserInput_14gtk","focusVisible":"input__focusVisible_14gtk","onautofillstart":"input__onautofillstart_14gtk","onautofillcancel":"input__onautofillcancel_14gtk"};
require('./index.css')

var invertedColors = {"input":"input__input_1jrsh","hasInnerLabel":"input__hasInnerLabel_1jrsh","disableUserInput":"input__disableUserInput_1jrsh","error":"input__error_1jrsh"};
require('./inverted.css')

var colorCommonStyles = {
    default: defaultColors,
    inverted: invertedColors,
};
var SIZE_TO_CLASSNAME_MAP = {
    s: 'size-48',
    m: 'size-56',
    l: 'size-64',
    xl: 'size-72',
    40: 'size-40',
    48: 'size-48',
    56: 'size-56',
    64: 'size-64',
    72: 'size-72',
};
var inputTypesForSelectionRange = ['password', 'search', 'tel', 'text', 'url'];
var BaseInput = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var _d = _a.size, size = _d === void 0 ? 48 : _d, _e = _a.type, type = _e === void 0 ? 'text' : _e, _f = _a.block, block = _f === void 0 ? false : _f, _g = _a.colors, colors = _g === void 0 ? 'default' : _g, bottomAddons = _a.bottomAddons, dataTestId = _a.dataTestId, _h = _a.clear, clear = _h === void 0 ? false : _h, disabled = _a.disabled, error = _a.error, success = _a.success, hint = _a.hint, className = _a.className, fieldClassName = _a.fieldClassName, inputClassName = _a.inputClassName, labelClassName = _a.labelClassName, addonsClassName = _a.addonsClassName, rightAddonsProps = _a.rightAddonsProps, leftAddonsProps = _a.leftAddonsProps, focusedClassName = _a.focusedClassName, filledClassName = _a.filledClassName, label = _a.label, _j = _a.labelView, labelView = _j === void 0 ? 'inner' : _j, leftAddons = _a.leftAddons, onFocus = _a.onFocus, onBlur = _a.onBlur, onChange = _a.onChange, onClear = _a.onClear, onClick = _a.onClick, onMouseDown = _a.onMouseDown, onMouseUp = _a.onMouseUp, onAnimationStart = _a.onAnimationStart, rightAddons = _a.rightAddons, value = _a.value, defaultValue = _a.defaultValue, wrapperRef = _a.wrapperRef, readOnlyProp = _a.readOnly, FormControlComponent = _a.FormControlComponent, disableUserInput = _a.disableUserInput, restProps = __rest(_a, ["size", "type", "block", "colors", "bottomAddons", "dataTestId", "clear", "disabled", "error", "success", "hint", "className", "fieldClassName", "inputClassName", "labelClassName", "addonsClassName", "rightAddonsProps", "leftAddonsProps", "focusedClassName", "filledClassName", "label", "labelView", "leftAddons", "onFocus", "onBlur", "onChange", "onClear", "onClick", "onMouseDown", "onMouseUp", "onAnimationStart", "rightAddons", "value", "defaultValue", "wrapperRef", "readOnly", "FormControlComponent", "disableUserInput"]);
    var onKeyDown = restProps.onKeyDown;
    var uncontrolled = value === undefined;
    var readOnly = readOnlyProp || disableUserInput;
    var inputRef = useRef(null);
    var focusVisible = useFocus(inputRef, 'keyboard')[0];
    var _k = useState(restProps.autoFocus), focused = _k[0], setFocused = _k[1];
    var _l = useState(defaultValue || ''), stateValue = _l[0], setStateValue = _l[1];
    var filled = Boolean(uncontrolled ? stateValue : value);
    var _m = useState(false), autofilled = _m[0], setAutofilled = _m[1];
    // отображаем крестик только для заполненного и активного инпута
    var clearButtonVisible = clear && filled && !disabled && !readOnlyProp;
    var hasInnerLabel = label && labelView === 'inner';
    useLayoutEffect_SAFE_FOR_SSR(function () {
        var input = inputRef.current;
        if (!restProps.autoFocus ||
            !input ||
            // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange
            !inputTypesForSelectionRange.includes(input.type)) {
            return;
        }
        // https://github.com/facebook/react/issues/14125
        input.setSelectionRange(input.value.length, input.value.length);
    }, []);
    var handleInputFocus = useCallback(function (event) {
        if (!readOnlyProp || disableUserInput) {
            setFocused(true);
        }
        if (onFocus) {
            onFocus(event);
        }
    }, [onFocus, readOnlyProp, disableUserInput]);
    var handleInputBlur = useCallback(function (event) {
        setFocused(false);
        if (onBlur) {
            onBlur(event);
        }
    }, [onBlur]);
    var handleInputChange = useCallback(function (event) {
        var inputValue = event.target.value;
        var target = event.target;
        var isInputTypeNumber = target.getAttribute('type') === 'number';
        var pattern = /[eE]/g;
        if (isInputTypeNumber && pattern.test(inputValue)) {
            inputValue = inputValue.replace(pattern, '');
        }
        if (onChange) {
            onChange(event, { value: inputValue });
        }
        if (uncontrolled) {
            setStateValue(inputValue);
        }
    }, [onChange, uncontrolled]);
    var handleKeyDown = useCallback(function (event) {
        /**
         * По умолчанию в input[type=number] можно вводить числа типа 2e5 (200 000)
         * Это ломает некоторое поведение, поэтому запрещаем ввод символов [eE]
         * @see DS-6808
         */
        var key = event.key, target = event.target;
        var eventTarget = target;
        var isInputTypeNumber = eventTarget.getAttribute('type') === 'number';
        if (isInputTypeNumber && (key === 'e' || key === 'E')) {
            event.preventDefault();
            return;
        }
        if (onKeyDown) {
            onKeyDown(event);
        }
    }, [onKeyDown]);
    var handleClear = useCallback(function (event) {
        if (!clearButtonVisible)
            return;
        if (uncontrolled) {
            setStateValue('');
        }
        if (onClear) {
            onClear(event);
        }
        if (inputRef.current && !focused) {
            inputRef.current.focus();
        }
    }, [clearButtonVisible, focused, onClear, uncontrolled]);
    var handleAnimationStart = useCallback(function (event) {
        if (onAnimationStart) {
            onAnimationStart(event);
        }
        setAutofilled(event.animationName.includes('start'));
    }, [onAnimationStart]);
    var renderRightAddons = function () {
        var addonsVisible = clearButtonVisible || rightAddons || error || success;
        return (addonsVisible && (React.createElement(Fragment, null,
            clearButtonVisible && (React.createElement(ClearButton, { onClick: handleClear, disabled: disabled, colors: colors, dataTestId: getDataTestId(dataTestId, 'clear-icon'), size: size })),
            rightAddons,
            error && (React.createElement("div", { className: cn(styles.errorIcon, styles["size-".concat(size)]), "data-addon": 'error-icon' },
                React.createElement(StatusBadge, { view: 'negative-alert', size: size === 40 ? 16 : 20, dataTestId: getDataTestId(dataTestId, 'error-icon') }))),
            success && !error && (React.createElement("div", { className: cn(styles.successIcon, styles["size-".concat(size)]) },
                React.createElement(StatusBadge, { view: 'positive-checkmark', size: size === 40 ? 16 : 20, dataTestId: getDataTestId(dataTestId, 'success-icon') }))))));
    };
    return FormControlComponent ? (React.createElement(FormControlComponent, { ref: wrapperRef, className: cn(className, focused && focusedClassName, filled && filledClassName), fieldClassName: cn(fieldClassName, (_b = {},
            _b[styles.focusVisible] = focusVisible,
            _b)), labelClassName: labelClassName, addonsClassName: addonsClassName, size: size, colors: colors, block: block, disabled: disabled, readOnly: readOnlyProp, filled: filled || autofilled || focused, focused: focused, error: error, label: label, labelView: labelView, hint: hint, leftAddons: leftAddons, rightAddons: renderRightAddons(), bottomAddons: bottomAddons, onClick: onClick, onMouseDown: onMouseDown, onMouseUp: onMouseUp, dataTestId: getDataTestId(dataTestId, 'form-control'), rightAddonsProps: rightAddonsProps, leftAddonsProps: leftAddonsProps },
        React.createElement("input", __assign({}, restProps, { className: cn(styles.input, styles["size-".concat(size)], colorCommonStyles[colors].input, (_c = {},
                _c[styles.disableUserInput] = disableUserInput,
                _c[colorCommonStyles[colors].disableUserInput] = disableUserInput,
                _c[colorCommonStyles[colors].error] = error,
                _c[styles[SIZE_TO_CLASSNAME_MAP[size]]] = hasInnerLabel,
                _c[styles.hasInnerLabel] = hasInnerLabel,
                _c[colorCommonStyles[colors].hasInnerLabel] = hasInnerLabel,
                _c), inputClassName), disabled: disabled, onBlur: handleInputBlur, onFocus: handleInputFocus, onChange: handleInputChange, onKeyDown: handleKeyDown, onAnimationStart: handleAnimationStart, ref: mergeRefs([ref, inputRef]), type: type, value: uncontrolled ? stateValue : value, readOnly: readOnly, "data-test-id": dataTestId, "aria-label": typeof label === 'string' ? label : undefined })))) : null;
});

export { BaseInput };
